<script setup lang="ts">
    import { computed, ref, reactive } from 'vue';
    import type { SurveyProgressAnswer, SurveyQuestionModel } from '@/types/model';
    import type { ISurveyButton, ISurveySlider, ITheSurveyMultipleChoiceComponent, ITheSurveyPageComponent } from '@/types/components';
    import TheSurveyMultipleChoice from './TheSurveyMultipleChoice.vue';
    import TheSurveyInputField from './TheSurveyInputField.vue';
    import SurveyButton from './SurveyButton.vue';
    import SurveySlider from './SurveySlider.vue';
    import { useSurveyStore } from '@/store';
    import { QuestionCategoryResponseEnum, SurveyComponentQuestionTypeEnum, SurveyComponentTypeEnum } from '@/constants/survey';

    const props = defineProps({
        showHeaderTextBold: {
            type: Boolean,
            default: false
        }
    });
    const surveyModule = useSurveyStore();
    
    const emit = defineEmits(['onNextClick', 'onDeclineClick', 'update:selectedValues']);

    const multipleChoiceAnswers = ref<ITheSurveyMultipleChoiceComponent[]>([]);
    const sliderAnswers = ref<ISurveySlider[]>([]);
    const buttonAnswers = ref<ISurveyButton[]>([]);
    const inputFieldAnswers = ref<Record<number, any>>({});
    
    const currentPage = computed(() => surveyModule.currentPage);
    
     // Initialize selectedValues with empty objects for the multiple choice depths
     let selectedValues = reactive({ 0: {}, 1: {}, 2: {} });
     selectedValues[currentPage.value.renderingConfig.order] = {};

     // Initialize "Why we ask this" dialog visibility per question
     const showInfoModal = ref<Record<number, boolean>>({});
     currentPage.value.questions.forEach((question) => {
         showInfoModal.value[question.questionId] = false;
     });

     const setInputFieldRef = (el: any, questionId: number) => {
         if (el) {
             inputFieldAnswers.value[questionId] = el;
         } else {
             delete inputFieldAnswers.value[questionId];
         }
     };

     const isMultipleChoice = (question: SurveyQuestionModel) => {
        return (
            question.renderingConfig.type === SurveyComponentQuestionTypeEnum.choice &&
            question.permittedAnswers.length !== 0
        );
    };

    const isButton = (question: SurveyQuestionModel) => {
        return (
            question.renderingConfig.type === SurveyComponentQuestionTypeEnum.button &&
            question.permittedAnswers.length !== 0
        );
    };

    const isSlider = (question: SurveyQuestionModel) => {
        return question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider;
    };

    const isInputField = (question: SurveyQuestionModel) => {
        return question.inputFieldConfig !== undefined;
    };

    const validate = async () => {
        let valid = true;
        if (multipleChoiceAnswers.value) {
            const results = await Promise.all(
                multipleChoiceAnswers.value.map((mc) => mc.validate())
            );
            valid = results.every((valid) => valid);
        }

        if (inputFieldAnswers.value) {
            const inputResults = await Promise.all(
                Object.values(inputFieldAnswers.value).map((input: any) => input.validate())
          );
          valid = inputResults.every((result) => result) && valid;
        }

        return valid;
    };

    const getResponses = () => {
        let responses: SurveyProgressAnswer[] = [];

        let multiplechoiceAnswers: SurveyProgressAnswer[] = [];
        let sliderAnswersResponses: SurveyProgressAnswer[] = [];
        let buttonAnswersResponses: SurveyProgressAnswer[] = [];
        let inputFieldAnswersResponses: SurveyProgressAnswer[] = [];

        currentPage.value.questions.forEach((question) => {
            if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.choice) {
                multiplechoiceAnswers = multipleChoiceAnswers.value
                    .map((mca) => mca.getResponses())
                    .reduce((acc, val) => acc.concat(val), []);
            } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider) {
                sliderAnswersResponses = sliderAnswers.value.map((sa) => sa.getResponse());
            } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.button) {
                buttonAnswersResponses = buttonAnswers.value.map((ba) => ba.getResponse());
            } else if (isInputField(question)) {
                const questionValue = selectedValues[currentPage.value.renderingConfig.order]?.[question.questionId];
                if (questionValue !== undefined && questionValue !== null) {
                    inputFieldAnswersResponses.push({
                        question,
                        permittedAnswer: {
                            answerId: questionValue.toString(),
                            name: questionValue.toString(),
                            renderingConfig: {
                                type: SurveyComponentTypeEnum.textbox,
                                exclusiveChoice: false,
                                isDeclining: false,
                                vid: '',
                                order: 0,
                                isRecorded: true,
                                hoistAnswerToParentFreeResponseField: false,
                                dropdownItems: null
                            }
                        },
                        value: questionValue.toString()
                    });
                }
            }
        });

        responses = [...multiplechoiceAnswers, ...sliderAnswersResponses, ...buttonAnswersResponses, ...inputFieldAnswersResponses];

        return responses;
    };

    const isIconRadioQuestion = (question: SurveyQuestionModel) =>
        question.permittedAnswers.some(
            a => a.renderingConfig.type === SurveyComponentTypeEnum.iconRadio
        );

    defineExpose({
        getResponses,
        validate
    });
</script>

<template>
    <div v-for="question in currentPage.questions"
         class="row survey__question"
         :key="question.questionId">
        <div class="col-12">

            <!-- Question Image -->
            <template v-if="question.renderingConfig.imageUrl">
                <div class="row justify-center q-pb-xl">
                    <q-img width="75%" :src="question.renderingConfig.imageUrl" />
                </div>
            </template>

            <!-- Question Header -->
            <template v-if="question.renderingConfig.showHeader">
                <h3 v-if="question.questionHeader"
                    v-html="question.questionHeader"
                    class="survey__question-header" />
            </template>

            <!-- Question Text -->
            <p v-if="question.questionText"
               v-html="question.questionText"
               :class="[
                   'survey__question-text',
                   {'text-weight-bold': showHeaderTextBold && question.questionHeader},
                   {'font-size-18' : question.questionCategory === QuestionCategoryResponseEnum.MultipleChoiceConsent}
               ]" />

            <!-- Question Sub-Text -->
            <p v-if="question.questionSubText"
               v-html="question.questionSubText"
               class="survey__question-subtext" />

            <!-- Answer Options -->
            <div v-if="isMultipleChoice(question)"
                 :class="['survey__answer', { 'survey__answer--icon-radio': isIconRadioQuestion(question) }]">
                <TheSurveyMultipleChoice v-for="answer in question.permittedAnswers" v-model="selectedValues" :depth="0"
                                         :key="`${question.questionId}-${answer.answerId}`" :permittedAnswer="answer" :question="question"
                                         ref="multipleChoiceAnswers" data-testid="multipleChoiceAnswer" />
            </div>

             <!-- Input Field -->
             <div v-if="isInputField(question)" class="survey__answer">
                 <TheSurveyInputField 
                    v-model="selectedValues"
                    :question="question"
                    :input-field-config="question.inputFieldConfig"
                    :ref="(el) => setInputFieldRef(el, question.questionId)" 
                />
             </div>

            <!-- Button(s) -->
            <template v-if="isButton(question)">
                <SurveyButton v-model="selectedValues"
                              @on-decline-click="emit('onDeclineClick')"
                              @on-next-click="emit('onNextClick')"
                              :question="question"
                              ref="buttonAnswers" />
            </template>

            <!-- Slider -->
            <template v-if="isSlider(question)">
                <SurveySlider v-model="selectedValues"
                              :question="question"
                              ref="sliderAnswers" />
            </template>
        </div>

        <!-- Details Footer Text -->
         <div v-if="question.renderingConfig.detailsFooterText"
             v-html="question.renderingConfig.detailsFooterText.htmlBody"
             :class="['col-12', {
                 'survey__details-footer--highlight-box': question.renderingConfig.detailsFooterText.style === 'HighlightBox'
             }]"
             data-testid="detailsFooterText" />

        <!-- Info Modal -->
        <div v-if="question.renderingConfig.infoModal"
             class="col-12 pt-16">

            <!-- Trigger -->
            <zbm-btn @click="showInfoModal[question.questionId] = true"
                     class="info-modal-trigger"
                     data-testid="infoModalLink"
                     icon="fa-regular fa-circle-question"
                     :outline="false"
                     text-color="content-trg-inline-link">
              <span v-html="question.renderingConfig.infoModal.link || question.renderingConfig.infoModal.title" />
            </zbm-btn>
          
            <!-- Dialog -->
            <zbm-dialog v-model="showInfoModal[question.questionId]"
                        :close-button="true">

              <!-- Body -->
              <div class="row q-dialog__body">
                <div class="col">
                  <div v-html="question.renderingConfig.infoModal.body"
                       class="text-left" />
                </div>
              </div>
            </zbm-dialog>
        </div>
    </div>    

    <div v-if="currentPage.footer" class="row survey__question">
        <div class="col">
            <p v-html="currentPage.footer" class="survey__footer" />
        </div>
    </div>
</template>

<style scoped lang="scss">
    @use 'sass:map';

    .survey {

        // Question
        &__question { // .row

            &:not(:last-child) {
                margin-block-end: 60px;
            }

            // Header
            &-header { // <h3>
                margin-block-end: 15px;

                &:empty {
                    display: none;
                }
            }

            // Text
            &-text { // <p>
                font-size: map.get($font-sizes, '22');
                line-height: 30px;

                .italic { // <span>
                    font-size: map.get($font-sizes, '18');
                }
            }

            // SubText
            &-subtext { // <p>
                font-size: map.get($font-sizes, '18');
                line-height: 30px;
            }
          
          // Info Modal - Trigger
          .info-modal-trigger { // .q-btn
            font-weight: map.get($text-weights, regular);
            font-size: map.get($font-sizes, '16');
            
            :deep(.q-icon) {
              margin-inline-end: map.get(map.get($spaces, 'xs'), x);
            }
          }
        }

        // Details Footer Text
        &__details-footer { // .col-12

            // HighlightBox
            &--highlight-box {
                margin-block-start: 16px;
                padding-block: 12px;
                padding-inline: 12px;
                color: $primitives-action-100;
                background-color: $primitives-action-10;
                border-radius: 12px;

                :deep(p), 
                :deep(li) {

                    &:not(:last-child) {
                        margin-block-end: 8px;
                    }
                }
            }
        }

        // Footer
        &__footer { // <p>
          font-size: map.get($font-sizes, '14');
          color: $content-txt-subdued;
          text-align: center;
          padding-block-start: 40px;
        }
    }
</style>
